// Metadata rute tanpa zod (method, path, auth) untuk client frontend yang ringan.
// Harus identik dengan `memberRoutes`/`adminRoutes` (dijaga route-meta.test.ts); skema lengkap ada di member.ts/admin.ts.

export interface RouteMeta {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  auth: boolean;
  idempotent?: true;
  multipart?: true;
}

export const memberRouteMeta = {
  devPayPackage: { method: 'POST', path: '/__dev/fake-pg/pay/:id', auth: true },
  catalog: { method: 'GET', path: '/catalog', auth: false },
  uploadKtp: { method: 'POST', path: '/ocr/ktp', auth: false, multipart: true },
  register: { method: 'POST', path: '/auth/register', auth: false },
  checkReferral: { method: 'GET', path: '/auth/referral-codes/:code', auth: false },
  // DECISION-PENDING(Q10): OTP registrasi nonaktif sementara.
  // resendOtp: { method: 'POST', path: '/auth/otp/resend', auth: false },
  // verifyOtp: { method: 'POST', path: '/auth/otp/verify', auth: false },
  login: { method: 'POST', path: '/auth/login', auth: false },
  verifyLogin: { method: 'POST', path: '/auth/login/verify', auth: false },
  resendLoginOtp: { method: 'POST', path: '/auth/login/resend', auth: false },
  refresh: { method: 'POST', path: '/auth/refresh', auth: false },
  logout: { method: 'POST', path: '/auth/logout', auth: false },
  forgotPassword: { method: 'POST', path: '/auth/password/forgot', auth: false },
  resetPassword: { method: 'POST', path: '/auth/password/reset', auth: false },
  me: { method: 'GET', path: '/me', auth: true },
  updateMe: { method: 'PATCH', path: '/me', auth: true },
  acceptTerms: { method: 'POST', path: '/me/terms', auth: true },
  completeKtp: { method: 'POST', path: '/me/ktp', auth: true },
  uploadPhoto: { method: 'POST', path: '/me/photo', auth: true, multipart: true },
  changePassword: { method: 'PATCH', path: '/me/password', auth: true },
  changePhone: { method: 'POST', path: '/me/phone', auth: true },
  confirmPhone: { method: 'POST', path: '/me/phone/verify', auth: true },
  bankAccounts: { method: 'GET', path: '/me/bank-accounts', auth: true },
  addBankAccount: { method: 'POST', path: '/me/bank-accounts', auth: true },
  removeBankAccount: { method: 'DELETE', path: '/me/bank-accounts/:id', auth: true },
  trees: { method: 'GET', path: '/trees', auth: true },
  createTree: { method: 'POST', path: '/trees', auth: true },
  renameTree: { method: 'PATCH', path: '/trees/:id', auth: true },
  // DECISION-PENDING(Q19): kode referral sementara hanya diubah admin lewat CMS.
  // changeReferralCode: { method: 'PATCH', path: '/trees/:id/referral-code', auth: true },
  // checkReferralCodeAvailability: { method: 'GET', path: '/referral-codes/:code/check', auth: true },
  dashboard: { method: 'GET', path: '/trees/:id/dashboard', auth: true },
  network: { method: 'GET', path: '/trees/:id/network', auth: true },
  tier: { method: 'GET', path: '/trees/:id/tier', auth: true },
  wallets: { method: 'GET', path: '/trees/:id/wallets', auth: true },
  ledger: { method: 'GET', path: '/trees/:id/ledger', auth: true },
  platforms: { method: 'GET', path: '/platforms', auth: true },
  createLink: { method: 'POST', path: '/trees/:id/affiliate-links', auth: true },
  orders: { method: 'GET', path: '/trees/:id/orders', auth: true },
  order: { method: 'GET', path: '/orders/:id', auth: true },
  orderByItem: { method: 'GET', path: '/order-items/:id', auth: true },
  createPackageOrder: { method: 'POST', path: '/trees/:id/package-orders', auth: true },
  packageOrder: { method: 'GET', path: '/package-orders/:id', auth: true },
  uploadPackagePaymentProof: {
    method: 'POST',
    path: '/package-orders/:id/payment-proof',
    auth: true,
    multipart: true,
  },
  withdrawalQuote: { method: 'GET', path: '/trees/:id/withdrawals/quote', auth: true },
  requestWithdrawalOtp: { method: 'POST', path: '/trees/:id/withdrawals/otp', auth: true },
  createWithdrawal: { method: 'POST', path: '/trees/:id/withdrawals', auth: true, idempotent: true },
  withdrawals: { method: 'GET', path: '/trees/:id/withdrawals', auth: true },
  taxSlips: { method: 'GET', path: '/tax-slips', auth: true },
  taxSlipDownload: { method: 'GET', path: '/tax-slips/:id/download', auth: true },
  notifications: { method: 'GET', path: '/notifications', auth: true },
  readNotification: { method: 'POST', path: '/notifications/:id/read', auth: true },
} as const satisfies Record<string, RouteMeta>;

export const adminRouteMeta = {
  login: { method: 'POST', path: '/admin/auth/login', auth: false },
  // DECISION-PENDING(Q16): MFA admin nonaktif sementara.
  // verifyMfa: { method: 'POST', path: '/admin/auth/mfa', auth: false },
  logout: { method: 'POST', path: '/admin/auth/logout', auth: true },
  me: { method: 'GET', path: '/admin/me', auth: true },
  menus: { method: 'GET', path: '/admin/me/menus', auth: true },
  dashboard: { method: 'GET', path: '/admin/dashboard', auth: true },
  members: { method: 'GET', path: '/admin/members', auth: true },
  member: { method: 'GET', path: '/admin/members/:accountId', auth: true },
  ktpPhoto: { method: 'GET', path: '/admin/members/:accountId/ktp-photo', auth: true },
  treeNetwork: { method: 'GET', path: '/admin/trees/:treeId/network', auth: true },
  tierHistory: { method: 'GET', path: '/admin/trees/:treeId/tier-history', auth: true },
  suspendMember: { method: 'POST', path: '/admin/members/:accountId/suspend', auth: true },
  activateMember: { method: 'POST', path: '/admin/members/:accountId/activate', auth: true },
  resetMemberPassword: { method: 'POST', path: '/admin/members/:accountId/reset-password', auth: true },
  correctIdentity: { method: 'PATCH', path: '/admin/members/:accountId/identity', auth: true },
  updateTreeReferralCode: { method: 'PATCH', path: '/admin/trees/:treeId/referral-code', auth: true },
  treeReferralCodeAvailability: {
    method: 'GET',
    path: '/admin/trees/:treeId/referral-code-availability',
    auth: true,
  },
  setTreeReferralCodeStatus: {
    method: 'PATCH',
    path: '/admin/trees/:treeId/referral-code-status',
    auth: true,
  },
  treeReferralCodeHistory: {
    method: 'GET',
    path: '/admin/trees/:treeId/referral-code-history',
    auth: true,
  },
  nikReviews: { method: 'GET', path: '/admin/nik-reviews', auth: true },
  nikReviewKtpPhoto: { method: 'GET', path: '/admin/nik-reviews/:id/ktp-photo', auth: true },
  resolveNikReview: { method: 'POST', path: '/admin/nik-reviews/:id/resolve', auth: true },
  configCurrent: { method: 'GET', path: '/admin/configs/current', auth: true },
  configReleases: { method: 'GET', path: '/admin/configs/releases', auth: true },
  settings: { method: 'GET', path: '/admin/settings', auth: true },
  updateSetting: { method: 'PUT', path: '/admin/settings/:key', auth: true },
  configVersions: { method: 'GET', path: '/admin/configs/:category/versions', auth: true },
  configCompare: { method: 'GET', path: '/admin/configs/:category/compare', auth: true },
  configSave: { method: 'PUT', path: '/admin/configs/:category', auth: true },
  manualTransferQris: { method: 'GET', path: '/admin/configs/payment/qris', auth: true },
  uploadManualTransferQris: {
    method: 'POST',
    path: '/admin/configs/payment/qris',
    auth: true,
    multipart: true,
  },
  incentiveSimulate: { method: 'POST', path: '/admin/configs/incentive/simulate', auth: true },
  platforms: { method: 'GET', path: '/admin/platforms', auth: true },
  createPlatform: { method: 'POST', path: '/admin/platforms', auth: true },
  updatePlatform: { method: 'PATCH', path: '/admin/platforms/:id', auth: true },
  deactivatePlatform: { method: 'DELETE', path: '/admin/platforms/:id', auth: true },
  importCsv: { method: 'POST', path: '/admin/platforms/:id/imports', auth: true, multipart: true },
  importStatement: { method: 'POST', path: '/admin/platforms/:id/statements', auth: true, multipart: true },
  unattributedOrders: { method: 'GET', path: '/admin/unattributed-orders', auth: true },
  attributeOrder: { method: 'POST', path: '/admin/unattributed-orders/:id/attribute', auth: true },
  reconciliationRuns: { method: 'GET', path: '/admin/reconciliation-runs', auth: true },
  reconciliationRun: { method: 'GET', path: '/admin/reconciliation-runs/:id', auth: true },
  runReconciliation: { method: 'POST', path: '/admin/reconciliation-runs', auth: true },
  orders: { method: 'GET', path: '/admin/orders', auth: true },
  order: { method: 'GET', path: '/admin/orders/:id', auth: true },
  simulateOrder: { method: 'GET', path: '/admin/orders/:id/simulate', auth: true },
  reviewQueue: { method: 'GET', path: '/admin/review-queue', auth: true },
  reinstateOrder: { method: 'POST', path: '/admin/orders/:id/reinstate', auth: true },
  adjustOrder: { method: 'POST', path: '/admin/orders/:id/adjustments', auth: true },
  treeLedger: { method: 'GET', path: '/admin/trees/:treeId/ledger', auth: true },
  packageOrders: { method: 'GET', path: '/admin/package-orders', auth: true },
  packageOrder: { method: 'GET', path: '/admin/package-orders/:id', auth: true },
  recordPackageRefund: {
    method: 'POST',
    path: '/admin/package-orders/:id/payments/:paymentId/refund',
    auth: true,
  },
  confirmManualPackagePayment: { method: 'POST', path: '/admin/package-orders/manual-payments', auth: true },
  confirmPackageOrder: { method: 'POST', path: '/admin/package-orders/:id/confirm-payment', auth: true },
  rejectPackagePaymentProof: {
    method: 'POST',
    path: '/admin/package-orders/:id/payment-proofs/:proofId/reject',
    auth: true,
  },
  withdrawals: { method: 'GET', path: '/admin/withdrawals', auth: true },
  withdrawal: { method: 'GET', path: '/admin/withdrawals/:id', auth: true },
  retryWithdrawal: { method: 'POST', path: '/admin/withdrawals/:id/retry', auth: true },
  financeSummary: { method: 'GET', path: '/admin/finance/summary', auth: true },
  importDisbursementReport: {
    method: 'POST',
    path: '/admin/finance/disbursement-reports',
    auth: true,
    multipart: true,
  },
  taxRecords: { method: 'GET', path: '/admin/tax/records', auth: true },
  taxRecord: { method: 'GET', path: '/admin/tax/records/:id', auth: true },
  issueTaxSlip: { method: 'POST', path: '/admin/tax/records/:id/issue', auth: true },
  cancelTaxSlip: { method: 'POST', path: '/admin/tax/records/:id/cancel', auth: true },
  amendTaxSlip: { method: 'POST', path: '/admin/tax/records/:id/amend', auth: true },
  importTaxSlipNumbers: { method: 'POST', path: '/admin/tax/slip-numbers', auth: true, multipart: true },
  taxRecap: { method: 'GET', path: '/admin/tax/recap', auth: true },
  taxSimulate: { method: 'GET', path: '/admin/tax/simulate', auth: true },
  reports: { method: 'GET', path: '/admin/reports', auth: true },
  report: { method: 'GET', path: '/admin/reports/:type', auth: true },
  requestExport: { method: 'POST', path: '/admin/exports', auth: true },
  exportStatus: { method: 'GET', path: '/admin/exports/:id', auth: true },
  auditLogs: { method: 'GET', path: '/admin/audit-logs', auth: true },
  auditChainStatus: { method: 'GET', path: '/admin/audit-logs/chain-status', auth: true },
  auditLog: { method: 'GET', path: '/admin/audit-logs/:id', auth: true },
  admins: { method: 'GET', path: '/admin/admins', auth: true },
  createAdmin: { method: 'POST', path: '/admin/admins', auth: true },
  updateAdmin: { method: 'PATCH', path: '/admin/admins/:id', auth: true },
  resetAdminMfa: { method: 'POST', path: '/admin/admins/:id/reset-mfa', auth: true },
  roles: { method: 'GET', path: '/admin/roles', auth: true },
  createRole: { method: 'POST', path: '/admin/roles', auth: true },
  updateRole: { method: 'PATCH', path: '/admin/roles/:id', auth: true },
  deleteRole: { method: 'DELETE', path: '/admin/roles/:id', auth: true },
} as const satisfies Record<string, RouteMeta>;
